import { useEffect, useState } from "react";
import { Download, Loader2, TrendingDown, TrendingUp } from "lucide-react";
import { Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import api from "@/lib/api";
import { btnGhost, downloadFile, inp, lbl } from "./ui";

const COLORS = ["#D4AF37", "#7dd3fc", "#34d399", "#f472b6", "#a78bfa", "#fb923c", "#facc15", "#94a3b8"];

export default function AnalyticsPanel({ types }) {
  const [q, setQ] = useState({ period: "month", event_type: "", date_from: "", date_to: "" });
  const [d, setD] = useState(null);
  const params = Object.fromEntries(Object.entries(q).filter(([, v]) => v));
  useEffect(() => {
    setD(null);
    api.get("/attendance/analytics", { params }).then((r) => setD(r.data)).catch(() => toast.error("Could not load analytics"));
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k) => (e) => setQ({ ...q, [k]: e.target.value });
  const exportXlsx = () => {
    const { period, ...rest } = params; // eslint-disable-line no-unused-vars
    const qs = new URLSearchParams(rest).toString();
    downloadFile(api, `/attendance/analytics/export.xlsx?${qs}`, "HOH-Attendance-history.xlsx").catch(() => toast.error("Export failed"));
  };

  return (
    <div data-testid="att-analytics">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
        <div><label className={lbl}>Group by</label>
          <select data-testid="att-an-period" className={inp} value={q.period} onChange={set("period")}>
            <option value="week">Weekly</option><option value="month">Monthly</option><option value="year">Yearly</option>
          </select></div>
        <div><label className={lbl}>Event type</label>
          <select data-testid="att-an-type" className={inp} value={q.event_type} onChange={set("event_type")}>
            <option value="">All types</option>{types.map((t) => <option key={t}>{t}</option>)}
          </select></div>
        <div><label className={lbl}>From</label><input type="date" data-testid="att-an-from" className={inp} value={q.date_from} onChange={set("date_from")} /></div>
        <div><label className={lbl}>To</label><input type="date" data-testid="att-an-to" className={inp} value={q.date_to} onChange={set("date_to")} /></div>
        <button onClick={exportXlsx} data-testid="att-an-export" className={btnGhost}><Download size={14} /> Excel</button>
      </div>
      {!d ? <Loader2 className="animate-spin text-[#D4AF37] mt-8" /> : <Charts d={d} />}
    </div>
  );
}

function Charts({ d }) {
  const g = d.summary.latest_growth_pct;
  const data = d.series.map((s) => ({ period: s.period, Average: s.average, Total: s.total, ...s.by_category }));
  if (!d.series.length) return <p className="text-neutral-500 text-sm mt-8" data-testid="att-an-empty">No counted events in this range yet. Open an event and start counting to see trends.</p>;
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <Kpi label="Events" value={d.summary.events} testid="att-kpi-events" />
        <Kpi label="Total attendance" value={d.summary.total} testid="att-kpi-total" />
        <Kpi label="Average per event" value={d.summary.average} testid="att-kpi-average" />
        <Kpi label={`Change vs previous ${d.period}`} testid="att-kpi-growth"
          value={g == null ? "—" : <span className={`inline-flex items-center gap-1 ${g >= 0 ? "text-emerald-300" : "text-red-300"}`}>{g >= 0 ? <TrendingUp size={20} /> : <TrendingDown size={20} />}{g}%</span>} />
      </div>
      <div className="grid lg:grid-cols-2 gap-5 mt-5">
        <ChartCard title="Average attendance per event">
          <LineChart data={data}><CartesianGrid stroke="#ffffff10" vertical={false} /><XAxis dataKey="period" tick={{ fill: "#888", fontSize: 10 }} /><YAxis tick={{ fill: "#888", fontSize: 10 }} allowDecimals={false} />
            <Tooltip contentStyle={{ background: "#111", border: "1px solid #333", fontSize: 12 }} /><Line type="monotone" dataKey="Average" stroke="#D4AF37" strokeWidth={2} dot={{ r: 3 }} /></LineChart>
        </ChartCard>
        <ChartCard title="Attendance by category">
          <BarChart data={data}><CartesianGrid stroke="#ffffff10" vertical={false} /><XAxis dataKey="period" tick={{ fill: "#888", fontSize: 10 }} /><YAxis tick={{ fill: "#888", fontSize: 10 }} allowDecimals={false} />
            <Tooltip contentStyle={{ background: "#111", border: "1px solid #333", fontSize: 12 }} /><Legend wrapperStyle={{ fontSize: 11 }} />
            {d.categories.map((c, i) => <Bar key={c} dataKey={c} stackId="a" fill={COLORS[i % COLORS.length]} />)}</BarChart>
        </ChartCard>
      </div>
      <div className="overflow-x-auto mt-6">
        <table className="w-full text-sm" data-testid="att-an-table">
          <thead><tr className="border-b border-white/10 text-xs tracking-widest uppercase text-neutral-400"><th className="text-left py-3">Period</th><th className="text-right">Events</th><th className="text-right">Total</th><th className="text-right">Average</th><th className="text-right">Change</th></tr></thead>
          <tbody>{[...d.series].reverse().map((s) => (
            <tr key={s.period} className="border-b border-white/5"><td className="py-2.5 text-white">{s.period}</td><td className="text-right">{s.events}</td><td className="text-right">{s.total}</td><td className="text-right text-[#D4AF37]">{s.average}</td>
              <td className={`text-right ${s.growth_pct == null ? "text-neutral-500" : s.growth_pct >= 0 ? "text-emerald-300" : "text-red-300"}`}>{s.growth_pct == null ? "—" : `${s.growth_pct}%`}</td></tr>
          ))}</tbody>
        </table>
      </div>
    </>
  );
}

const ChartCard = ({ title, children }) => (
  <div className="luxury-card rounded-xl p-5"><div className="text-xs tracking-widest uppercase text-neutral-400 mb-3">{title}</div>
    <div className="h-64"><ResponsiveContainer width="100%" height="100%">{children}</ResponsiveContainer></div></div>
);
const Kpi = ({ label, value, testid }) => (
  <div className="luxury-card rounded-xl p-5"><div className="text-[10px] tracking-widest uppercase text-neutral-500">{label}</div>
    <div data-testid={testid} className="font-serif-display text-3xl text-white mt-1">{value}</div></div>
);
